import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import { allComics } from '@/mocks/comics';
import Breadcrumb from '@/components/feature/Breadcrumb';

const READ_PAGES = [
  {
    src: 'https://readdy.ai/api/search-image?query=Manga%20comic%20page%20art%20style%2C%20dynamic%20action%20scene%20with%20two%20characters%20fighting%2C%20Japanese%20manga%20style%20black%20and%20white%20lineart%2C%20warm%20cream%20paper%20tone%2C%20clean%20white%20background%2C%20vertical%20comic%20page%20layout%20with%20speed%20lines%2C%20detailed%20illustration&width=900&height=1300&seq=read-pg-01&orientation=portrait',
    alt: '漫画阅读页 1',
  },
  {
    src: 'https://readdy.ai/api/search-image?query=Manga%20comic%20page%2C%20dialogue%20scene%20between%20two%20characters%20talking%2C%20Japanese%20manga%20style%20lineart%2C%20warm%20soft%20cream%20paper%20tone%2C%20clean%20white%20background%2C%20vertical%20comic%20page%20layout%2C%20expressive%20faces%20and%20speech%20bubbles&width=900&height=1300&seq=read-pg-02&orientation=portrait',
    alt: '漫画阅读页 2',
  },
  {
    src: 'https://readdy.ai/api/search-image?query=Manga%20comic%20page%2C%20dramatic%20reveal%20moment%20with%20shocked%20character%20face%2C%20black%20and%20white%20lineart%20Japanese%20manga%20style%2C%20warm%20cream%20tone%20background%2C%20vertical%20comic%20page%20with%20large%20impact%20panel&width=900&height=1300&seq=read-pg-03&orientation=portrait',
    alt: '漫画阅读页 3',
  },
  {
    src: 'https://readdy.ai/api/search-image?query=Manga%20comic%20page%2C%20emotional%20character%20close-up%20portrait%20with%20tears%2C%20Japanese%20manga%20art%20style%20lineart%2C%20warm%20gentle%20cream%20tone%2C%20clean%20white%20composition%2C%20vertical%20comic%20layout%2C%20detailed%20expression&width=900&height=1300&seq=read-pg-04&orientation=portrait',
    alt: '漫画阅读页 4',
  },
];

export default function ComicReadPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const comic = allComics.find((c) => c.id === id);
  const chapterParam = searchParams.get('chapter');

  const initialChapter = chapterParam ? parseInt(chapterParam, 10) : 1;
  const [currentChapter, setCurrentChapter] = useState(initialChapter);
  const [showNav, setShowNav] = useState(false);

  useEffect(() => {
    if (chapterParam) {
      setCurrentChapter(parseInt(chapterParam, 10));
    }
  }, [chapterParam]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentChapter]);

  const handleChapterChange = useCallback((chapterNum: number) => {
    setCurrentChapter(chapterNum);
    setShowNav(false);
  }, []);

  if (!comic) {
    return (
      <main className="pt-16 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center">
            <i className="ri-emotion-sad-line text-foreground-300 text-5xl"></i>
          </div>
          <h1 className="text-foreground-900 font-heading text-xl mb-2">漫画未找到</h1>
          <Link
            to="/"
            className="bg-primary-500 hover:bg-primary-600 text-white px-6 py-2.5 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors"
          >
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  const totalChapters = comic.chapters.length;
  const isFirst = currentChapter <= 1;
  const isLast = currentChapter >= totalChapters;

  const prevChapter = comic.chapters.find((ch) => ch.number === currentChapter - 1);
  const nextChapter = comic.chapters.find((ch) => ch.number === currentChapter + 1);

  return (
    <main className="pt-16 bg-background-50 min-h-screen">
      <Breadcrumb
        items={[
          { label: '首页', path: '/' },
          { label: '分类浏览', path: `/category/${comic.category}` },
          { label: comic.title, path: `/comic/${comic.id}` },
          { label: `第${currentChapter}话` },
        ]}
      />

      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-xl text-foreground-900">
              {comic.title}
            </h1>
            <p className="text-foreground-500 text-sm mt-1">
              第 {currentChapter} 话 · 共 {totalChapters} 话
            </p>
          </div>
          <button
            onClick={() => setShowNav(!showNav)}
            className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-100 border border-background-200/70 text-foreground-600 hover:text-foreground-900 transition-colors cursor-pointer"
          >
            <i className="ri-list-unordered text-lg"></i>
          </button>
        </div>

        {showNav && (
          <div className="mb-6 bg-background-100 rounded-xl border border-background-200/70 p-4 max-h-64 overflow-y-auto">
            <div className="grid grid-cols-5 sm:grid-cols-8 gap-2">
              {comic.chapters.map((ch) => (
                <button
                  key={ch.id}
                  onClick={() => handleChapterChange(ch.number)}
                  className={`px-2 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all cursor-pointer ${
                    currentChapter === ch.number
                      ? 'bg-primary-500 text-white'
                      : 'text-foreground-600 hover:bg-background-200/80 border border-background-200/70'
                  }`}
                >
                  {ch.number}
                </button>
              ))}
            </div>
          </div>
        )}

        <p className="text-foreground-500 text-xs text-center mb-6 bg-background-100 rounded-lg py-2 px-4 border border-background-200/70">
          <i className="ri-information-line mr-1"></i>
          下拉式连续阅读模式：向下滚动即可连续阅读，畅享流畅漫画体验
        </p>

        <div className="flex flex-col items-center gap-0">
          {READ_PAGES.map((page, idx) => (
            <div key={idx} className="w-full">
              <img
                src={page.src}
                alt={`${comic.title} 第${currentChapter}话 - ${page.alt} - 布丁漫画下拉式连续阅读`}
                title={`${comic.title} 布丁漫画下拉式免费阅读 第${currentChapter}话`}
                className="w-full h-auto"
              />
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between mt-8 pt-6 border-t border-background-200/70">
          {!isFirst && prevChapter ? (
            <Link
              to={`/comic/${comic.id}/read?chapter=${prevChapter.number}`}
              className="flex items-center gap-1 text-foreground-600 hover:text-primary-500 text-sm font-medium transition-colors cursor-pointer"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-arrow-left-s-line"></i>
              </div>
              上一话
            </Link>
          ) : (
            <div />
          )}

          <Link
            to={`/comic/${comic.id}`}
            className="text-foreground-500 hover:text-foreground-700 text-sm font-medium transition-colors cursor-pointer"
          >
            返回详情
          </Link>

          {!isLast && nextChapter ? (
            <Link
              to={`/comic/${comic.id}/read?chapter=${nextChapter.number}`}
              className="flex items-center gap-1 text-foreground-600 hover:text-primary-500 text-sm font-medium transition-colors cursor-pointer"
            >
              下一话
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-arrow-right-s-line"></i>
              </div>
            </Link>
          ) : (
            <div />
          )}
        </div>

        <div className="mt-8 text-center">
          <Link
            to={`/comic/${comic.id}`}
            className="inline-flex items-center gap-1 bg-background-100 hover:bg-background-200/80 text-foreground-600 border border-background-200/70 px-6 py-2.5 rounded-full text-sm font-medium transition-all cursor-pointer"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line"></i>
            </div>
            返回漫画详情
          </Link>
        </div>
      </div>
    </main>
  );
}